import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import Editor, { DEFAULT_TEXT } from "../../../../../components/editor/Editor";
import IconUploader from "../../../../../components/pickers/icon-uploader/IconUploader";
import Select from "../../../../../components/select/Select";
import { ChannelMenuAction, ChatSettingsChannelMenu, ChatSettingsChannelMenuInput } from "../../../../../__generated__/api";
import ExpandingSection from "../../../../../components/expanding-section/ExpandingSection";

const ACTION_TYPES = {
    [ChannelMenuAction.Disabled]: "Disabled",
    [ChannelMenuAction.CreatePublicTicket]: "Create a public ticket and thread",
    [ChannelMenuAction.CreatePrivateTicket]: "Create a private ticket and thread",
    [ChannelMenuAction.TextOnly]: "Only display floating text",
    [ChannelMenuAction.StartDirectMessage]: "Start a direct message",
};

export interface Props {
    data?: ChatSettingsChannelMenu,
    onChange: (menu: ChatSettingsChannelMenuInput) => void;
}

const DEFAULT_MENU_TEXT = "Press \"Create Request\" to get started...";

export default function ChannelMenuSettings({ data, onChange }: Props): ReactElement {
    const [action, setAction] = useState(() => data?.action ?? ChannelMenuAction.Disabled);

    const [menuText, setMenuText] = useState(() => data?.menuText?.rawText ?? DEFAULT_MENU_TEXT);
    const [menuButtonText, setMenuButtonText] = useState(() => data?.menuButtonText ?? "");

    const [afterText, setAfterText] = useState(() => data?.afterText?.rawText ?? "");
    const [afterImageUrl, setAfterImageUrl] = useState<string | undefined>(() => data?.afterImageUrl ?? "");

    const [welcomeMessage, setWelcomeMessage] = useState(() => data?.welcomeMessage?.rawText ?? "");

    useEffect(() => {
        setAction(data?.action ?? ChannelMenuAction.Disabled);
        setMenuText(data?.menuText?.rawText ?? DEFAULT_MENU_TEXT);
        setMenuButtonText(data?.menuButtonText ?? "");
        setAfterText(data?.afterText?.rawText ?? "");
        setAfterImageUrl(data?.afterImageUrl ?? "");
        setWelcomeMessage(data?.welcomeMessage?.rawText ?? "");
    }, [data?.action, data?.menuText?.rawText, data?.menuButtonText,
        data?.afterText?.rawText, data?.afterImageUrl, data?.welcomeMessage?.rawText]);

    useEffect(() => {
        if (data?.action !== action
            || data.menuText?.rawText !== menuText
            || data.menuButtonText !== menuButtonText
            || data.afterText?.rawText !== afterText
            || data.welcomeMessage?.rawText !== welcomeMessage) {
            onChange({
                action,
                menuText: !menuText ? undefined : { rawText: menuText },
                menuButtonText,
                afterText: !afterText ? undefined : { rawText: afterText },
                afterImageUrl,
                welcomeMessage: !welcomeMessage ? undefined : { rawText: welcomeMessage },
            });
        }
    }, [action, menuText, menuButtonText, afterText, afterImageUrl, welcomeMessage]);

    return (
        <div className="border p-3 rounded bg-white mt-4">
            <h5 className="mb-3 text-light">
                Channel Menu
            </h5>

            <div className="text-light">
                This option allows you to configure a message and menu
                that will always live at the bottom of the chat. If a user
                types a message in a channel, the channel menu will automatically
                move to the bottom.

            </div>

            <Form.Group className="mb-3 mt-3">
                <Form.Label className="fw-bolder">
                    Action
                </Form.Label>

                <Select
                    className="text-gray"
                    placeholder=""
                    onChange={value => setAction(value.value as ChannelMenuAction)}
                    options={Object.entries(ACTION_TYPES).map(([value, label]) => ({ value, label }))}
                    value={!action ? undefined : {
                        value: action,
                        label: ACTION_TYPES[action],
                    }}
                />
                <Form.Text>
                    What to do when a user clicks the button.
                </Form.Text>
            </Form.Group>
            {action !== ChannelMenuAction.Disabled && (
                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">Channel Text</Form.Label>
                    <div className="bg-white border-light border rounded">
                        <Editor
                            text={menuText as string || DEFAULT_TEXT}
                            onChange={msg => setMenuText(msg)}
                            isScrollDisabled
                            shouldFocus={false}
                            placeholder={"Press \"Create Request\" to get started..."}
                        />
                    </div>
                    <Form.Text>
                        The main text to display in the channel.
                    </Form.Text>
                </Form.Group>
            )}

            {action !== ChannelMenuAction.Disabled && action !== ChannelMenuAction.TextOnly && (
                <ExpandingSection title="More Settings" classBackground="bg-gray-100">

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bolder">Button Text</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Create Request"
                            onChange={evt => setMenuButtonText(evt.target.value)}
                            value={menuButtonText}
                        />
                        <Form.Text>
                            The text of the button that the user will click
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bolder">Welcome Message</Form.Label>
                        <div className="bg-white border-light border rounded">
                            <Editor
                                text={welcomeMessage as string || DEFAULT_TEXT}
                                onChange={msg => setWelcomeMessage(msg)}
                                isScrollDisabled
                                shouldFocus={false}
                                placeholder="Thanks for submitting the request..."
                            />
                        </div>
                        <Form.Text>
                            This message overrides the standard welcome message in the channel.
                        </Form.Text>
                    </Form.Group>

                    <h5 className="mt-5 text-light">After Click (Optional)</h5>
                    <p className="text-light">
                        To help the user understand the next steps, you can add an image and text that will be
                        send to the user after they
                        {" "}
                        {action === ChannelMenuAction.StartDirectMessage ? "click start" : "fill out the request"}
                        . The text should say something about how they can continue in the direct message sent from
                        the Agent bot (or whatever you renamed it to).
                    </p>

                    <Form.Group className="mb-4">
                        <IconUploader
                            value={afterImageUrl as string}
                            onChange={icon => setAfterImageUrl(icon)}
                            shouldStore
                            size="xl"
                        />
                        <Form.Text>
                            This image should help the user visually understand their next steps.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <div className="bg-white border-light border rounded">
                            <Editor
                                text={afterText as string || DEFAULT_TEXT}
                                onChange={msg => setAfterText(msg)}
                                isScrollDisabled
                                shouldFocus={false}
                                placeholder="To continue, please view the Agent..."
                            />
                        </div>
                        <Form.Text>
                            This message should inform the user of next steps.
                        </Form.Text>
                    </Form.Group>

                </ExpandingSection>
            )}

        </div>
    );
}

ChannelMenuSettings.defaultProps = {
    data: undefined,
};
